import React from "react";
import { Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * FieldRenderer — renders one prompt-task field (or one Brand Profile field).
 *
 * props
 *   field      { id|key, label, type, placeholder, help, required, options, default, span, brandKey }
 *   value      current string value
 *   onChange   (nextValue) => void
 *   error      optional error string
 *   fromProfile true when the value was auto-filled from the Brand Profile (shows a small indicator)
 */
export default function FieldRenderer({ field, value = "", onChange, error, fromProfile = false }) {
  const id = field.id || field.key;
  const label = field.label;
  const common = {
    id,
    placeholder: field.placeholder,
    "aria-invalid": !!error,
    className: cn(error && "border-destructive focus-visible:ring-destructive"),
  };

  let control;
  switch (field.type) {
    case "textarea":
    case "urls":
      control = <Textarea {...common} rows={field.type === "urls" ? 3 : 3} value={value} onChange={(e) => onChange(e.target.value)} />;
      break;
    case "select":
      control = (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger id={id} className={common.className} aria-invalid={!!error}>
            <SelectValue placeholder={field.placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    case "number":
      control = <Input {...common} type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} />;
      break;
    case "url":
      control = <Input {...common} type="url" value={value} onChange={(e) => onChange(e.target.value)} />;
      break;
    case "tags":
      control = <Input {...common} value={value} onChange={(e) => onChange(e.target.value)} />;
      break;
    default:
      control = <Input {...common} type="text" value={value} onChange={(e) => onChange(e.target.value)} />;
  }

  return (
    <div className={cn("space-y-1.5", field.span === "full" && "md:col-span-2")}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="text-sm">
          {label}
          {field.required && <span className="ml-0.5 text-destructive" aria-hidden="true">*</span>}
        </Label>
        {fromProfile && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-600 dark:text-violet-300">
            <Sparkles className="h-3 w-3" /> From brand profile
          </span>
        )}
      </div>
      {control}
      {field.type === "tags" && !error && <p className="text-xs text-muted-foreground">Separate items with commas.</p>}
      {field.help && !error && <p className="text-xs text-muted-foreground">{field.help}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
